import { SafeLink } from "@/components/ui/SafeLink";
import { Button } from "@/components/ui/button";
import { SlideFromLeft, SlideFromRight } from "@/components/ui/animations";

export default function Tiles() {
    return (
        <div className="flex flex-col w-full bg-gradient-to-b from-gray-50 to-gray-100 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-32 h-32 bg-red-500/3 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-gray-900/2 rounded-full blur-3xl"></div>
            </div>

            {/* Furniture & Décor */}
            <SlideFromLeft Component={
                <div id="furniture-and-decor" className="md:pl-0 lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col  lg:flex-row items-center gap-8 md:gap-10 lg:gap-12 justify-center">
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/products/furniture-bedding-office-workspace5.jpg`}
                                alt="Corporate workspace featuring desks, chairs, storage and greenery"
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-8 lg:px-0 px-4 text-center  lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">Furniture </span> & Décor
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                Outfit your workspace with furniture that works as hard as your team. Whether you’re fitting out agile offices, executive lounges, breakout zones, or a welcoming lobby, we’ll help you choose pieces that look sharp and last the distance. Tell us what you need—ergonomic chairs, bold tables, statement sofas, or custom storage—and we’ll bring it all together, seamlessly.
                            </p>

                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* Appliances */}
            <SlideFromRight Component={
                <div id="appliances" className="md:pl-0 lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col  lg:flex-row-reverse items-center gap-8 md:gap-10 lg:gap-12 justify-center">                        
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/NewWebPhotos/Appliances/hnc-web-kitchen-appliances-category.jpg`}
                                alt="Corporate kitchen setup featuring integrated appliances"
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center  lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">Appliances
                                </span>
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                We make it easy to specify, source, and deliver energy-efficient appliances for staff kitchens, break rooms, offices, and communal areas. We supply reliable, efficient brands that are built to perform under pressure, from the morning rush to after-hours catch-ups. One call, and it’s all sorted.
                            </p>                            
                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* AV Solutions */}
            <SlideFromLeft Component={
                <div id="av-solutions" className="md:pl-0 lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col  lg:flex-row items-center gap-8 md:gap-10 lg:gap-12 justify-center">                        
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/sector/corporate-government-audio-visual-meeting-solutions.jpg`}
                                alt="Collaborative workspace with AV technology"
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center  lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">AV </span> Solutions
                                <br />
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                Make meetings effortless and collaboration second nature. Our AV systems are tailored for the real world: crystal-clear calls, presentations that pop, and tech that just works—every time. From huddle rooms to conference halls, we design and install audio-visual that keeps your teams connected and in control.
                            </p>                            
                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* Boardroom & Meeting Room */}
            <SlideFromRight Component={
                <div id="boardroom-meeting-room" className="lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col  lg:flex-row-reverse items-center gap-8 md:gap-10 lg:gap-12 justify-center">                        
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/products/furniture-bedding-office-conference6.jpg`}
                                alt="Meeting room setup with ergonomic chairs and AV display"
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center  lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">Boardroom & Meeting Room </span> Solutions
                            </h2>

                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                No more “can you hear me?” moments. Transform your boardroom into a space where ideas flow and decisions happen. We’ll handle the layout, the furniture, the screens, the sound—so your meetings make an impact, not a headache.
                            </p>                            
                        </div>
                    </div>
                </div>
            } />
        </div>
    )
}